import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { platformService } from '@/services/platformService'

const clp = (n: number) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n || 0)
const pct = (n: number | null | undefined) => (n === null || n === undefined) ? '—' : `${n}%`

function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: string }) {
    return (
        <div className="rounded-xl border border-slate-800 bg-slate-800/30 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
            <div className={`mt-1 text-xl font-semibold ${tone || 'text-slate-100'}`}>{value}</div>
            {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
        </div>
    )
}

const emptyForm = {
    tenantId: '', planCode: '', amountClp: '', bank: '', transferDate: '',
    referenceNumber: '', receiptUrl: '', periodYear: '', periodMonth: '', dteType: '33', notes: '', billingEmail: '',
}

export default function PlatformBillingPage() {
    const [status, setStatus] = useState<{ enabled: boolean; invoiceGeneration: boolean; flow: boolean; libreDTE: boolean } | null>(null)
    const [metrics, setMetrics] = useState<any | null>(null)
    const [plans, setPlans] = useState<any[]>([])
    const [subs, setSubs] = useState<any[]>([])
    const [invoices, setInvoices] = useState<any[]>([])
    const [payments, setPayments] = useState<any[]>([])
    const [tenants, setTenants] = useState<any[]>([])
    const [form, setForm] = useState({ ...emptyForm })
    const [saving, setSaving] = useState(false)
    const [uploadingReceipt, setUploadingReceipt] = useState(false)

    const reload = () => {
        platformService.billingMetrics().then(setMetrics).catch(() => { /* noop */ })
        platformService.billingSubscriptions().then(setSubs).catch(() => setSubs([]))
        platformService.billingInvoices().then(setInvoices).catch(() => setInvoices([]))
        platformService.billingPayments().then(setPayments).catch(() => setPayments([]))
    }

    useEffect(() => {
        platformService.billingStatus().then(setStatus).catch(() => { /* noop */ })
        platformService.billingPlans().then(setPlans).catch(() => setPlans([]))
        platformService.listTenants().then(setTenants).catch(() => setTenants([]))
        reload()
    }, [])

    const onPlan = (code: string) => {
        const p = plans.find((x) => x.code === code)
        setForm((f) => ({ ...f, planCode: code, amountClp: p && p.priceCLP ? String(p.priceCLP) : f.amountClp }))
    }

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.tenantId || !form.planCode || !form.amountClp) return toast.error('Tenant, plan y monto son obligatorios')
        setSaving(true)
        try {
            const payload: any = {
                tenantId: form.tenantId,
                planCode: form.planCode,
                amountClp: Number(form.amountClp),
                bank: form.bank || undefined,
                transferDate: form.transferDate || undefined,
                referenceNumber: form.referenceNumber || undefined,
                receiptUrl: form.receiptUrl || undefined,
                notes: form.notes || undefined,
                billingEmail: form.billingEmail || undefined,
                periodYear: form.periodYear ? Number(form.periodYear) : undefined,
                periodMonth: form.periodMonth ? Number(form.periodMonth) : undefined,
                dteType: Number(form.dteType),
            }
            const r = await platformService.billingManualCharge(payload)
            toast.success(`Cobro registrado · factura ${r.invoiceNumber} PAID${r.dteEmitted ? ' · DTE emitido' : ' · sin DTE (LibreDTE off)'}`)
            setForm({ ...emptyForm })
            reload()
        } catch (err: any) {
            toast.error(err?.response?.data?.error || 'Error al registrar el cobro')
        } finally {
            setSaving(false)
        }
    }

    const onReceiptFile = async (file?: File) => {
        if (!file) return
        setUploadingReceipt(true)
        try {
            const r = await platformService.billingUploadReceipt(file)
            setForm((f) => ({ ...f, receiptUrl: r.key }))
            toast.success('Comprobante subido al almacén soberano')
        } catch (e: any) {
            toast.error(e?.response?.data?.error || 'Error al subir el comprobante')
        } finally {
            setUploadingReceipt(false)
        }
    }

    const openReceipt = async (ref: string) => {
        if (/^https?:\/\//i.test(ref)) { window.open(ref, '_blank'); return }
        try {
            const blob = await platformService.billingReceiptBlob(ref)
            window.open(URL.createObjectURL(blob), '_blank')
        } catch {
            toast.error('No se pudo abrir el comprobante')
        }
    }

    const inp = 'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100'

    return (
        <div className="space-y-8">
            <h1 className="text-2xl font-semibold">Finanzas</h1>

            {/* KPIs de alto nivel */}
            <section>
                <h2 className="mb-3 text-lg font-medium text-slate-200">Métricas (founder)</h2>
                {!metrics ? (
                    <p className="text-sm text-slate-400">Cargando métricas…</p>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                            <Kpi label="MRR" value={clp(metrics.mrr)} hint="Ingreso recurrente mensual" tone="text-emerald-300" />
                            <Kpi label="ARR" value={clp(metrics.arr)} hint="Anualizado" />
                            <Kpi label="ARPA" value={clp(metrics.arpa)} hint="Ingreso medio por cuenta" />
                            <Kpi label="Clientes de pago" value={String(metrics.activePaying)} />
                            <Kpi label="NRR" value={pct(metrics.nrr)} hint="Retención neta de ingresos" tone="text-cyan-300" />
                            <Kpi label="GRR" value={pct(metrics.grr)} hint="Retención bruta" />
                            <Kpi label="Logo churn" value={pct(metrics.logoChurnRate)} hint="Bajas del mes" tone="text-amber-300" />
                            <Kpi label="Conversión trial→pago" value={pct(metrics.trialConversionRate)} />
                            <Kpi label="Recaudado (mes)" value={clp(metrics.collectedThisMonth)} tone="text-emerald-300" />
                            <Kpi label="Recaudado (total)" value={clp(metrics.collectedAllTime)} />
                            <Kpi label="Por cobrar" value={clp(metrics.outstanding)} tone="text-amber-300" />
                            <Kpi label="LTV estimado" value={metrics.ltvEstimate ? clp(metrics.ltvEstimate) : '—'} hint="ARPA / churn" />
                        </div>
                        {metrics.note && <p className="mt-2 text-xs text-slate-500">ℹ️ {metrics.note}</p>}
                    </>
                )}
            </section>

            {/* Cobro manual (founder-only) */}
            <section>
                <h2 className="mb-1 text-lg font-medium text-slate-200">Cobro manual (transferencia)</h2>
                <p className="mb-3 text-sm text-slate-400">
                    Registra una transferencia: marca la factura <b>PAID</b>, activa/renueva la suscripción y emite DTE si LibreDTE está ON.
                    <b className="text-emerald-300"> Flow no se toca → cero comisión.</b>
                </p>
                <form onSubmit={submit} className="grid grid-cols-1 gap-3 rounded-xl border border-slate-800 bg-slate-800/30 p-4 md:grid-cols-3">
                    <label className="text-sm">Organización (tenant)
                        <select className={inp} value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })}>
                            <option value="">— Seleccionar —</option>
                            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name || t.slug}</option>)}
                        </select>
                    </label>
                    <label className="text-sm">Plan
                        <select className={inp} value={form.planCode} onChange={(e) => onPlan(e.target.value)}>
                            <option value="">— Seleccionar —</option>
                            {plans.map((p) => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
                        </select>
                    </label>
                    <label className="text-sm">Monto recibido (CLP, IVA incl.)
                        <input type="number" className={inp} value={form.amountClp} onChange={(e) => setForm({ ...form, amountClp: e.target.value })} placeholder="49990" />
                    </label>
                    <label className="text-sm">Banco
                        <input className={inp} value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} placeholder="Banco de Chile" />
                    </label>
                    <label className="text-sm">Fecha de transferencia
                        <input type="date" className={inp} value={form.transferDate} onChange={(e) => setForm({ ...form, transferDate: e.target.value })} />
                    </label>
                    <label className="text-sm">N° de referencia
                        <input className={inp} value={form.referenceNumber} onChange={(e) => setForm({ ...form, referenceNumber: e.target.value })} placeholder="Comprobante / folio" />
                    </label>
                    <label className="text-sm">Tipo DTE
                        <select className={inp} value={form.dteType} onChange={(e) => setForm({ ...form, dteType: e.target.value })}>
                            <option value="33">Factura (33)</option>
                            <option value="39">Boleta (39)</option>
                        </select>
                    </label>
                    <label className="text-sm">Período (año)
                        <input type="number" className={inp} value={form.periodYear} onChange={(e) => setForm({ ...form, periodYear: e.target.value })} placeholder="(actual)" />
                    </label>
                    <label className="text-sm">Período (mes)
                        <input type="number" min={1} max={12} className={inp} value={form.periodMonth} onChange={(e) => setForm({ ...form, periodMonth: e.target.value })} placeholder="(actual)" />
                    </label>
                    <label className="text-sm md:col-span-2">Comprobante (sube el archivo → S3 soberano, o pega una URL)
                        <div className="flex gap-2">
                            <input className={inp} value={form.receiptUrl} onChange={(e) => setForm({ ...form, receiptUrl: e.target.value })} placeholder="archivo subido o https://…" />
                            <label className="shrink-0 cursor-pointer rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700">
                                {uploadingReceipt ? 'Subiendo…' : 'Subir'}
                                <input type="file" className="hidden" disabled={uploadingReceipt}
                                    onChange={(e) => onReceiptFile(e.target.files?.[0] || undefined)} />
                            </label>
                        </div>
                    </label>
                    <label className="text-sm">Email de facturación
                        <input className={inp} value={form.billingEmail} onChange={(e) => setForm({ ...form, billingEmail: e.target.value })} placeholder="finanzas@cliente.cl" />
                    </label>
                    <label className="text-sm md:col-span-3">Notas
                        <input className={inp} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </label>
                    <div className="md:col-span-3">
                        <button disabled={saving} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50">
                            {saving ? 'Registrando…' : 'Registrar cobro manual'}
                        </button>
                    </div>
                </form>
            </section>

            {/* Estado de activación */}
            <div className={`rounded-xl border p-4 ${status?.enabled ? 'border-emerald-800 bg-emerald-900/20' : 'border-amber-800 bg-amber-900/20'}`}>
                <div className="font-medium text-slate-100">
                    {status?.enabled ? '✅ Facturación activa' : '⏸️ Facturación DESACTIVADA'}
                </div>
                <p className="mt-1 text-sm text-slate-400">
                    {status?.enabled
                        ? 'La emisión de facturas/DTE/cobros está habilitada.'
                        : 'El cobro manual funciona igual (registro interno + activación). La emisión de DTE al SII solo ocurre con BILLING_LIBREDTE_ENABLED=true y RUT certificado.'}
                </p>
                {status && (
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        {[['Facturas', status.invoiceGeneration], ['Flow.cl', status.flow], ['DTE/SII', status.libreDTE]].map(([l, on]) => (
                            <span key={l as string} className={`rounded-full px-2 py-0.5 ${on ? 'bg-emerald-900 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>
                                {l as string}: {on ? 'ON' : 'OFF'}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Planes */}
            <section>
                <h2 className="mb-3 text-lg font-medium text-slate-200">Planes</h2>
                <div className="overflow-hidden rounded-xl border border-slate-800">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-800/70 text-slate-300"><tr>
                            <th className="px-4 py-3">Plan</th><th className="px-4 py-3">Precio</th>
                            <th className="px-4 py-3">Usuarios</th><th className="px-4 py-3">Agencias</th><th className="px-4 py-3">Ops/mes</th>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-800">
                            {plans.length === 0 ? (
                                <tr><td colSpan={5} className="px-4 py-5 text-center text-slate-400">Sin planes</td></tr>
                            ) : plans.map((p) => (
                                <tr key={p.code} className="text-slate-200">
                                    <td className="px-4 py-2">{p.name} <span className="text-slate-500">({p.code})</span></td>
                                    <td className="px-4 py-2">{p.priceCLP ? clp(p.priceCLP) : '—'}</td>
                                    <td className="px-4 py-2">{p.maxUsers < 0 ? '∞' : p.maxUsers}</td>
                                    <td className="px-4 py-2">{p.maxAgencies < 0 ? '∞' : p.maxAgencies}</td>
                                    <td className="px-4 py-2">{p.maxOperationsPerMonth < 0 ? '∞' : p.maxOperationsPerMonth}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Suscripciones */}
            <section>
                <h2 className="mb-3 text-lg font-medium text-slate-200">Suscripciones</h2>
                <div className="overflow-hidden rounded-xl border border-slate-800">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-800/70 text-slate-300"><tr>
                            <th className="px-4 py-3">Tenant</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3">Renueva</th><th className="px-4 py-3">Contacto</th>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-800">
                            {subs.length === 0 ? (
                                <tr><td colSpan={5} className="px-4 py-5 text-center text-slate-400">Sin suscripciones</td></tr>
                            ) : subs.map((s, i) => (
                                <tr key={i} className="text-slate-200">
                                    <td className="px-4 py-2 font-mono text-xs text-slate-400">{s.tenantId?.slice(0, 8)}</td>
                                    <td className="px-4 py-2">{s.plan || '—'}</td>
                                    <td className="px-4 py-2">{s.status}</td>
                                    <td className="px-4 py-2 text-slate-400">{s.renewsAt ? s.renewsAt.slice(0, 10) : '—'}</td>
                                    <td className="px-4 py-2 text-slate-400">{s.contactEmail || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Pagos recientes */}
            <section>
                <h2 className="mb-3 text-lg font-medium text-slate-200">Pagos recientes</h2>
                {payments.length === 0 ? (
                    <p className="rounded-xl border border-slate-800 bg-slate-800/30 p-4 text-sm text-slate-400">Sin pagos registrados.</p>
                ) : (
                    <div className="overflow-hidden rounded-xl border border-slate-800">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-800/70 text-slate-300"><tr>
                                <th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Tenant</th><th className="px-4 py-3">Método</th><th className="px-4 py-3">Monto</th><th className="px-4 py-3">Banco</th><th className="px-4 py-3">Ref</th><th className="px-4 py-3">Comprobante</th><th className="px-4 py-3">Por</th>
                            </tr></thead>
                            <tbody className="divide-y divide-slate-800">
                                {payments.map((p) => (
                                    <tr key={p.id} className="text-slate-200">
                                        <td className="px-4 py-2 text-slate-400">{p.createdAt ? p.createdAt.slice(0, 10) : '—'}</td>
                                        <td className="px-4 py-2 font-mono text-xs text-slate-400">{p.tenantId?.slice(0, 8)}</td>
                                        <td className="px-4 py-2">{p.method}</td>
                                        <td className="px-4 py-2">{clp(p.amountClp)}</td>
                                        <td className="px-4 py-2 text-slate-400">{p.bank || '—'}</td>
                                        <td className="px-4 py-2 text-slate-400">{p.referenceNumber || '—'}</td>
                                        <td className="px-4 py-2">
                                            {p.receiptUrl
                                                ? <button onClick={() => openReceipt(p.receiptUrl)} className="text-cyan-400 hover:underline">ver</button>
                                                : <span className="text-slate-500">—</span>}
                                        </td>
                                        <td className="px-4 py-2 text-slate-400">{p.createdBy || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Facturas */}
            <section>
                <h2 className="mb-3 text-lg font-medium text-slate-200">Facturas</h2>
                {invoices.length === 0 ? (
                    <p className="rounded-xl border border-slate-800 bg-slate-800/30 p-4 text-sm text-slate-400">Sin facturas.</p>
                ) : (
                    <div className="overflow-hidden rounded-xl border border-slate-800">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-800/70 text-slate-300"><tr>
                                <th className="px-4 py-3">N°</th><th className="px-4 py-3">Tenant</th><th className="px-4 py-3">Monto</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3">Período</th>
                            </tr></thead>
                            <tbody className="divide-y divide-slate-800">
                                {invoices.map((inv, i) => (
                                    <tr key={i} className="text-slate-200">
                                        <td className="px-4 py-2">{inv.invoiceNumber || '—'}</td>
                                        <td className="px-4 py-2 font-mono text-xs text-slate-400">{inv.tenantId?.slice(0, 8)}</td>
                                        <td className="px-4 py-2">{clp(inv.amountTotal)}</td>
                                        <td className="px-4 py-2">{inv.status}</td>
                                        <td className="px-4 py-2 text-slate-400">{inv.period}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    )
}
